import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Textarea } from '@/components/ui/textarea'
import { useToast } from '@/hooks/use-toast'
import { Answer, useCustomerStore } from '@/stores/customer-store'
import { X } from 'lucide-react'
import { useEffect, useState } from 'react'

interface AnswerEditPanelProps {
    answer: Answer
    onClose: () => void
    onSave: (answer: Answer, customerText?: string, useSystemDefault?: boolean) => Promise<void>
}

interface AnswerVariant {
    id: string
    content: string
    defaultAnswer?: boolean
}

interface ConversationMessage {
    id: number
    isCustomer: boolean
    text: string
}

export function AnswerEditPanel({ answer, onClose, onSave }: AnswerEditPanelProps) {
    const { toast } = useToast()
    const { loadSystemAnswers } = useCustomerStore()
    const [isSaving, setIsSaving] = useState(false)
    const [isLoading, setIsLoading] = useState(true)

    // Determine if it's ANSWER or OPTION type
    const isAnswerType = !!answer.answerVariants

    // System answers loaded from backend
    const [systemAnswers, setSystemAnswers] = useState<AnswerVariant[]>([])

    // State for selected variant
    const [selectedVariantId, setSelectedVariantId] = useState<string>(() => {
        if (answer.overriden) {
            return 'custom'
        }
        if (answer.answerVariants && answer.answerVariants.length > 0) {
            return answer.answerVariants[0].id
        }
        return 'system'
    })

    // Custom answer text
    const [customAnswerText, setCustomAnswerText] = useState<string>(() => {
        if (isAnswerType) {
            // ANSWER type
            if (answer.overriden && answer.answerVariants && answer.answerVariants.length > 0) {
                return answer.answerVariants[0].content
            }
        } else {
            // OPTION type
            if (answer.enabled && answer.customerAnswerActive) {
                return answer.customerAnswerActive
            } else if (!answer.enabled && answer.customerAnswerInactive) {
                return answer.customerAnswerInactive
            }
        }
        return ''
    })

    // Load system answers on mount
    useEffect(() => {
        const loadAnswers = async () => {
            try {
                setIsLoading(true)

                if (isAnswerType) {
                    // ANSWER type: Load from systemanswers API
                    const systemAnswersData = await loadSystemAnswers(answer.id)
                    if (systemAnswersData.answerVariants) {
                        setSystemAnswers(systemAnswersData.answerVariants)

                        // Auto-select the default answer if not overridden
                        if (!answer.overriden) {
                            const defaultAnswer = systemAnswersData.answerVariants.find((v: AnswerVariant) => v.defaultAnswer)
                            if (defaultAnswer) {
                                setSelectedVariantId(defaultAnswer.id)
                            }
                        }
                    }
                } else {
                    // OPTION type: Use defaultAnswerActive/Inactive from answer object
                    const variants: AnswerVariant[] = []

                    if (answer.defaultAnswerActive) {
                        variants.push({
                            id: 'active',
                            content: answer.defaultAnswerActive,
                            defaultAnswer: true
                        })
                    }

                    if (answer.defaultAnswerInactive) {
                        variants.push({
                            id: 'inactive',
                            content: answer.defaultAnswerInactive,
                            defaultAnswer: false
                        })
                    }

                    setSystemAnswers(variants)

                    // Auto-select based on enabled state
                    if (answer.enabled) {
                        setSelectedVariantId('active')
                    } else if (variants.length > 0) {
                        setSelectedVariantId(variants[0].id)
                    }
                }
            } catch (error) {
                console.error('Failed to load system answers:', error)
                toast({
                    title: 'Fehler',
                    description: 'System-Antworten konnten nicht geladen werden.',
                    variant: 'destructive'
                })
            } finally {
                setIsLoading(false)
            }
        }

        if (answer.id) {
            loadAnswers()
        } else {
            setIsLoading(false)
        }
    }, [answer.id, isAnswerType, loadSystemAnswers, answer.overriden, answer.enabled, answer.defaultAnswerActive, answer.defaultAnswerInactive])

    // Get the current answer text based on selection
    const getCurrentAnswerText = (): string => {
        if (selectedVariantId === 'custom') {
            return customAnswerText
        }

        // Find selected system answer
        const selectedAnswer = systemAnswers.find(a => a.id === selectedVariantId)
        if (selectedAnswer) {
            return selectedAnswer.content
        }

        // Fallback to current answer variant
        if (answer.answerVariants && answer.answerVariants.length > 0) {
            return answer.answerVariants[0].content
        }

        return ''
    }

    // Parse example conversation from backend format
    const parseExampleConversation = (): ConversationMessage[] => {
        if (!answer.exampleConversation) {
            return []
        }

        const lines = answer.exampleConversation.split('\r\n')
        const messages: ConversationMessage[] = []
        let id = 0

        // Add initial "..." message
        messages.push({
            id: id++,
            isCustomer: true,
            text: '...'
        })

        const currentAnswerText = getCurrentAnswerText()

        for (const line of lines) {
            const match = line.match(/^([UA]):\s(.+)$/)
            if (match) {
                const [, speaker, text] = match
                let messageText = text.trim()

                // Replace <SYSTEMANSWER> with actual selected answer
                if (messageText === '<SYSTEMANSWER>') {
                    messageText = currentAnswerText
                }

                messages.push({
                    id: id++,
                    isCustomer: speaker === 'U',
                    text: messageText
                })
            }
        }

        return messages
    }

    const conversationMessages = parseExampleConversation()

    const handleUseSystemAnswer = (systemAnswer: AnswerVariant) => {
        setCustomAnswerText(systemAnswer.content)
        setSelectedVariantId('custom')
    }

    const handleSave = async () => {
        setIsSaving(true)
        try {
            const useSystemDefault = selectedVariantId !== 'custom'
            const customerText = selectedVariantId === 'custom' ? customAnswerText : ''

            await onSave(answer, customerText, useSystemDefault)

            toast({
                title: 'Erfolgreich gespeichert',
                description: 'Die Antwort wurde aktualisiert.',
            })
        } catch (error) {
            toast({
                title: 'Fehler',
                description: 'Die Antwort konnte nicht gespeichert werden.',
                variant: 'destructive'
            })
        } finally {
            setIsSaving(false)
        }
    }

    return (
        <div className="h-full flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b flex items-center justify-between">
                <div className="flex-1 min-w-0">
                    <h2 className="text-lg font-semibold text-gray-900 truncate">
                        {answer.title || answer.displayTitle || 'Antwort bearbeiten'}
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">
                        {answer.id?.startsWith('temp_') ? 'Neue Antwort' : 'Antwort bearbeiten'}
                    </p>
                </div>
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={onClose}
                    className="text-gray-500 hover:text-gray-900 flex-shrink-0"
                >
                    <X className="h-5 w-5" />
                </Button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
                {/* Description Card */}
                <Card className="border-2 border-gray-200">
                    <CardContent className="p-4">
                        <h3 className="text-sm font-semibold text-gray-700 mb-2">Beschreibung der Aktion</h3>
                        <p className="text-sm text-gray-600">
                            {answer.description || answer.notes || 'Keine Beschreibung verfügbar.'}
                        </p>
                    </CardContent>
                </Card>

                {/* Example Conversation */}
                {conversationMessages.length > 0 && (
                    <div>
                        <h3 className="text-sm font-semibold text-gray-700 mb-3">Beispielkonversation</h3>
                        <div className="space-y-3">
                            {conversationMessages.map((message) => (
                                <div key={message.id} className={`flex gap-3 ${message.isCustomer ? '' : 'justify-end'}`}>
                                    {message.isCustomer && (
                                        <div className="w-8 h-8 rounded-full bg-gray-600 flex items-center justify-center text-white text-sm flex-shrink-0">
                                            ...
                                        </div>
                                    )}
                                    <div className={`flex-1 ${message.isCustomer ? '' : 'flex justify-end'}`}>
                                        <div className={`${message.isCustomer ? 'bg-gray-800' : 'bg-brand-primary'} text-white rounded-lg px-4 py-2 inline-block max-w-[85%]`}>
                                            {message.text}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Answer Variant Selection */}
                {isLoading ? (
                    <div className="flex items-center justify-center py-8">
                        <div className="flex flex-col items-center gap-3">
                            <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-primary border-t-transparent"></div>
                            <p className="text-sm text-gray-500">Lade Antwortvarianten...</p>
                        </div>
                    </div>
                ) : (
                    <div>
                        <h3 className="text-sm font-semibold text-gray-700 mb-3">Antwortvariante auswählen</h3>
                        <RadioGroup
                            value={selectedVariantId}
                            onValueChange={setSelectedVariantId}
                            className="space-y-3"
                        >
                            {/* System Answers */}
                            {systemAnswers.map((systemAnswer, index) => (
                                <div key={systemAnswer.id} className="flex items-start space-x-3">
                                    <RadioGroupItem value={systemAnswer.id} id={systemAnswer.id} className="mt-1" />
                                    <div className="flex-1 space-y-2">
                                        <div className="flex items-center justify-between">
                                            <Label htmlFor={systemAnswer.id} className="font-medium text-gray-900 cursor-pointer flex items-center gap-2">
                                                {systemAnswer.defaultAnswer ? (
                                                    <>
                                                        Standard
                                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-brand-primary-light text-brand-primary">
                                                            Empfohlen
                                                        </span>
                                                    </>
                                                ) : (
                                                    `Template ${index}`
                                                )}
                                            </Label>
                                            {answer.editableByCustomer && (
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => handleUseSystemAnswer(systemAnswer)}
                                                    className="h-7 text-xs"
                                                >
                                                    Als Vorlage verwenden
                                                </Button>
                                            )}
                                        </div>
                                        <Textarea
                                            value={systemAnswer.content}
                                            disabled
                                            rows={3}
                                            className="text-sm bg-gray-50"
                                        />
                                    </div>
                                </div>
                            ))}

                            {/* Custom Answer */}
                            {answer.editableByCustomer && (
                                <div className="flex items-start space-x-3">
                                    <RadioGroupItem value="custom" id="custom" className="mt-1" />
                                    <div className="flex-1 space-y-2">
                                        <Label htmlFor="custom" className="font-medium text-gray-900 cursor-pointer">
                                            Eigene Antwort
                                        </Label>
                                        <p className="text-sm text-gray-500">
                                            Erstelle deine eigene angepasste Antwort
                                        </p>
                                        {selectedVariantId === 'custom' && (
                                            <div className="mt-2">
                                                <Textarea
                                                    value={customAnswerText}
                                                    onChange={(e) => setCustomAnswerText(e.target.value)}
                                                    placeholder="Gib deine eigene Antwort ein..."
                                                    rows={4}
                                                    className="text-sm"
                                                />
                                                <p className="text-xs text-gray-500 mt-1">
                                                    Tipp: Nutze den Button "Als Vorlage verwenden" bei einer System-Antwort, um diese als Ausgangspunkt zu nutzen.
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </RadioGroup>
                    </div>
                )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t flex items-center justify-between bg-gray-50">
                <Button variant="outline" onClick={onClose}>
                    Abbrechen
                </Button>
                <Button onClick={handleSave} disabled={isSaving}>
                    {isSaving ? 'Speichert...' : 'Speichern'}
                </Button>
            </div>


        </div>
    )
}
